<template>
<div>
  <v-form :value="valid">
  <v-dialog v-model="open" max-width="620px">
    <v-card>
      <v-card-title>行事設定</v-card-title>

      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="4" align-self="center">
              <div>イベント名*</div>
            </v-col>
            <v-col cols="8" align-self="center">
              <v-text-field v-model="eventName" variant="outlined" hide-details density="compact" color="primary"></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4" align-self="center">
              <div>クラス</div>
            </v-col>
            <v-col cols="8" align-self="center">
              <v-select
                :items="classes"
                v-model="classIds"
                item-title="className"
                item-value="classId"
                variant="outlined"
                density="compact"
                hide-details
                multiple
                chips
                color="primary"
              >
              </v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4" align-self="center">
              <div>終日</div>
            </v-col>
            <v-col cols="8" align-self="center">
              <v-checkbox v-model="isAllDay" class="mt-0" hide-details color="primary"></v-checkbox>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4" align-self="center">
              <div v-if="dialogType == 'create'">開始日*</div>
              <div v-else>日付</div>
            </v-col>
            <v-col cols="8" align-self="center">
              <date-picker v-model="fromDate"></date-picker>
            </v-col>
          </v-row>

          <v-row v-if="!isAllDay">
            <v-col cols="4" align-self="center">
              <div>開始時刻</div>
            </v-col>
            <v-col cols="8" align-self="center">
              <!-- <time-picker v-model="fromTime"></time-picker> -->
              <v-text-field
                v-model="fromTime"
                type="time"
                variant="outlined"
                density="compact"
                hide-details
                color="primary"
              >
              </v-text-field>
            </v-col>
          </v-row>

          <v-row v-if="dialogType == 'create' && isAllDay">
            <v-col cols="4" align-self="center">
              <div>終了日</div>
            </v-col>
            <v-col cols="8" align-self="center">
              <date-picker v-model="toDate"></date-picker>
            </v-col>
          </v-row>

          <v-row v-if="!isAllDay">
            <v-col cols="4" align-self="center">
              <div>終了時刻</div>
            </v-col>
            <v-col cols="8" align-self="center">
              <!-- <time-picker v-model="toTime"></time-picker> -->
              <v-text-field
                v-model="toTime"
                type="time"
                variant="outlined"
                density="compact"
                hide-details
                @input="formatTimeToText"
                color="primary"
              >
              </v-text-field>
            </v-col>
          </v-row>

        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-container>
          <v-row v-if="dialogType == 'create'">
            <v-btn @click="close" variant="text"color="grey">キャンセル</v-btn>
            <v-spacer />
            <v-btn @click="addEvent" :disabled="!valid" :loading="loading" color="primary" variant="flat">作成</v-btn>
          </v-row>

          <v-row v-else>
            <v-btn @click="close" variant="text"color="grey">キャンセル</v-btn>
            <v-btn @click="deleteEvent" :loading="loading" dark color="red" variant="flat">削除</v-btn>
            <v-spacer />
            <v-btn @click="updateEvent" :disabled="!valid" :loading="loading" color="primary" variant="flat">保存</v-btn>
          </v-row>
        </v-container>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </v-form>
</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import Swal from 'sweetalert2'
import DialogMixin from '@/mixins/DialogMixin'
import DatePicker from '@/components/atoms/DatePicker.vue'
import { eventEditModule } from '@/store/dataModules/eventEditModule'
import LocalDataService from '@/service/LocalDataService'
import { classModule } from '@/store/dataModules/classModule'
import ValidationMixin from '@/mixins/ValidationMixin'

type dialogType = "create" | "update";

export default defineComponent({
  name: 'EventDialogContainer',

  mixins: [DialogMixin, ValidationMixin],

  emits: ['update:is-open'],

  components: {
    DatePicker,
  },

  props: {
    dialogType: {
      type: String as PropType<dialogType>,
      default: "create"
    }
  },

  watch: {
    fromDate: {
      handler(val: string) {
        if (val) {
          this.toDate = val;
        }
      },
    },
    isAllDay: {
      handler(val: string) {
        if (val) {
          this.fromTime = "";
          this.toTime = "";
        }
      }
    },
    isOpen : {
      handler(val: boolean) {
        // 終日チェックボックスのfromTimeによる切り替え
        if (val) {
          if (this.fromTime) {
            this.isAllDay = false;
          } else {
            this.isAllDay = true;
          }
        }
      }
    },
  },

  data() {
    return {
      isAllDay: false,
      loading: false,
    }
  },

  computed: {
    classes: function(): any[] {
      return classModule.classes.map((c) => {
        return {
          className: `${c.year}年度 ${c.className}`,
          classId: c.classId,
        }
      });
    },
    eventName: {
      get() {
        return eventEditModule.eventName;
      },
      set(value: string) {
        eventEditModule.setEventName(value);
      }
    },
    classIds: {
      get() {
        return eventEditModule.classIds;
      },
      set(value: number[]) {
        eventEditModule.setClassIds(value);
      }
    },
    fromDate: {
      get() {
        return eventEditModule.fromDate;
      },
      set(value: string) {
        eventEditModule.setFromDate(value);
      }
    },
    toDate: {
      get() {
        return eventEditModule.toDate;
      },
      set(value: string) {
        eventEditModule.setToDate(value);
      }
    },
    fromTime: {
      get(): string {
        return eventEditModule.fromTime ? this.formatTextToTime(eventEditModule.fromTime) : '';
      },
      set(value: string) {
        eventEditModule.setFromTime(this.formatTimeToText(value));
      }
    },
    toTime: {
      get(): string {
        return eventEditModule.toTime ? this.formatTextToTime(eventEditModule.toTime) : '';
      },
      set(value: string) {
        eventEditModule.setToTime(this.formatTimeToText(value));
      }
    },
    valid: function(): boolean {
      if (!this.isAllDay) {
        return (
          this.fromTime != "" &&
          this.toTime != ""
        );
      }

      if (this.fromDate && this.toDate && this.eventName) {
        return true; 
      } else {
        return false;
      }
    }
  },

  methods: {
    async addEvent() {
      this.loading = true;

      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await eventEditModule.addEvent({
          nurseryId: nurseryId
        });
        if (response) {
          Swal.fire({ title: '予定を追加しました', icon: 'success' });
          //this.$emit('update:is-open', false);
          this.open = false;
        } else {
          Swal.fire({ title: '失敗しました', icon: 'error', });
        }
      }
      this.loading = false;
    },
    async updateEvent() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await eventEditModule.updateEvent({
          nurseryId: nurseryId,
        });
        if (response) {
          Swal.fire({ title: '予定を保存しました', icon: 'success' });
          //this.$emit('update:is-open', false);
          this.open = false;
        } else {
          Swal.fire({ title: '失敗しました', icon: 'error', });
        }
      }
      this.loading = false;
    },
    async deleteEvent() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await eventEditModule.deleteEvent({
          nurseryId: nurseryId,
        });
        if (response) {
          Swal.fire({ title: '予定を削除しました', icon: 'success' });
          //this.$emit('update:is-open', false);
          this.open = false;
        } else {
          Swal.fire({ title: '失敗しました', icon: 'error', });
        }
      }
      this.loading = false;
    },
    formatTimeToText(time: string): string {
      return time.replace(':', '');
    },
    formatTextToTime(text: string): string {
      return text.slice(0, 2) + ':' + text.slice(2);
    },
  }
})
</script>